'use client';

import { useBasket } from '~/context/basket-context';
import { QuantityInput } from './quantity-input';
import { useCallback, useEffect, useState } from 'react';
import { applyBasketChanges } from '~/actions/basket/basket-actions';
import { useDebounceCallback } from 'usehooks-ts';

type PlpQuantityProps = {
  minQuantity?: number;
  itemCode: string; // item code of the basket item
};

export const PlpQuantityInputInner = ({
  minQuantity = 1,
  itemCode,
}: PlpQuantityProps) => {
  const [quantity, setQuantity] = useState<number>(minQuantity ?? 1);
  const basket = useBasket();

  const row = basket.basket?.rows.find((row) => row.itemCode === itemCode);
  const rowQuantity = row?.quantity;

  useEffect(() => {
    if (rowQuantity !== undefined) {
      setQuantity(rowQuantity);
    }
  }, [rowQuantity]);

  const handleUpdateBasket = useCallback(
    async (value: number) => {
      await applyBasketChanges({
        items: [
          {
            itemCode,
            quantity: value,
          },
        ],
      });
      await basket.actions?.reload();
    },
    [basket.actions, itemCode],
  );

  const debounced = useDebounceCallback(handleUpdateBasket, 1000);

  return (
    <QuantityInput
      size="plp"
      quantity={quantity}
      minQuantity={minQuantity}
      onChange={(value) => {
        // If the quantity is less than the minimum quantity, set the quantity to 0
        if (value < minQuantity) {
          setQuantity(0);
          void debounced(0);
          return;
        }
        setQuantity(value);
        void debounced(value);
      }}
      showDeleteIcon
      disabled={basket.isInitialLoading}
    />
  );
};

export const PlpQuantityInput = ({
  itemCode,
  children,
  minQuantity,
}: PlpQuantityProps & { children?: React.ReactNode }) => {
  const basket = useBasket();
  const row = basket.basket?.rows.find((row) => row.itemCode === itemCode);

  if (!row) {
    return children;
  }

  return (
    <PlpQuantityInputInner itemCode={itemCode} minQuantity={minQuantity} />
  );
};
